import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../../App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  ArrowLeft,
  Pencil,
  Trash2,
  Factory,
  MapPin,
  MoreVertical,
  Grid3X3,
  Move,
  Boxes,
  Package,
  Warehouse,
  Truck,
  Building,
  Settings,
  ZoomIn,
  ZoomOut,
  RotateCcw
} from "lucide-react";
import { usePermissions } from "../../hooks/usePermissions";
import { handleApiError } from "../../utils/errorHandler";
import { useTranslation } from "../../contexts/I18nContext";

const TIPI_AREA = [
  { value: "produzione", label: "Produzione", color: "#3b82f6", icon: Factory },
  { value: "stoccaggio", label: "Stoccaggio", color: "#22c55e", icon: Warehouse },
  { value: "spedizione", label: "Spedizione", color: "#f59e0b", icon: Truck },
  { value: "uffici", label: "Uffici", color: "#8b5cf6", icon: Building },
  { value: "altro", label: "Altro", color: "#6b7280", icon: Grid3X3 }
];

export default function ProduzioneStabilimentiPage() {
  const { t } = useTranslation();
  const { tenant } = useParams();
  const navigate = useNavigate();
  const [stabilimenti, setStabilimenti] = useState([]);
  const [selectedStabilimento, setSelectedStabilimento] = useState(null);
  const [aree, setAree] = useState([]);
  const [materiali, setMateriali] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Dialog states
  const [showStabDialog, setShowStabDialog] = useState(false);
  const [showAreaDialog, setShowAreaDialog] = useState(false);
  const [editingStab, setEditingStab] = useState(null);
  const [editingArea, setEditingArea] = useState(null);
  
  // Layout editor state
  const [zoom, setZoom] = useState(1);
  const [selectedArea, setSelectedArea] = useState(null);
  const [dragging, setDragging] = useState(null);
  const canvasRef = useRef(null);
  
  // Form state
  const [stabForm, setStabForm] = useState({ codice: "", nome: "", indirizzo: "", larghezza_m: 100, altezza_m: 100, note: "" });
  const [areaForm, setAreaForm] = useState({ nome: "", tipo: "produzione", x: 10, y: 10, larghezza: 20, altezza: 15, colore: "#3b82f6", note: "" });

  const loadStabilimenti = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/tenant/produzione/stabilimenti");
      setStabilimenti(res.data);
      if (res.data.length > 0 && !selectedStabilimento) {
        setSelectedStabilimento(res.data[0]);
      }
    } catch (error) {
      
      handleApiError(error, t("errors.loadFailed"));
    } finally {
      setLoading(false);
    }
  }, [selectedStabilimento, t]);

  const loadAree = useCallback(async () => {
    if (!selectedStabilimento) return;
    try {
      const res = await api.get(`/tenant/produzione/stabilimenti/${selectedStabilimento.id}/aree`);
      setAree(res.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error("Load aree error:", error);
    }
  }, [selectedStabilimento]);

  const loadMateriali = useCallback(async () => {
    if (!selectedStabilimento) return;
    try {
      const res = await api.get(`/tenant/produzione/materiale?stabilimento_id=${selectedStabilimento.id}&ricevuto=true`);
      setMateriali(res.data);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') console.error("Load materiali error:", error);
    }
  }, [selectedStabilimento]);

  useEffect(() => {
    loadStabilimenti();
  }, [loadStabilimenti]);

  useEffect(() => {
    if (selectedStabilimento) {
      loadAree();
      loadMateriali();
    }
  }, [selectedStabilimento, loadAree, loadMateriali]);

  // Stabilimento handlers
  const openCreateStabDialog = () => {
    setEditingStab(null);
    setStabForm({ codice: "", nome: "", indirizzo: "", larghezza_m: 100, altezza_m: 100, note: "" });
    setShowStabDialog(true);
  };

  const openEditStabDialog = (stab) => {
    setEditingStab(stab);
    setStabForm({
      codice: stab.codice,
      nome: stab.nome,
      indirizzo: stab.indirizzo || "",
      larghezza_m: stab.larghezza_m || 100,
      altezza_m: stab.altezza_m || 100,
      note: stab.note || ""
    });
    setShowStabDialog(true);
  };

  const handleSaveStab = async () => {
    if (!stabForm.codice || !stabForm.nome) {
      toast.error("Codice e nome obbligatori");
      return;
    }
    try {
      if (editingStab) {
        await api.put(`/tenant/produzione/stabilimenti/${editingStab.id}`, stabForm);
        toast.success("Stabilimento aggiornato");
      } else {
        const res = await api.post("/tenant/produzione/stabilimenti", stabForm);
        toast.success("Stabilimento creato");
      }
      setShowStabDialog(false);
      loadStabilimenti();
    } catch (error) {
      handleApiError(error, t("errors.saveFailed"));
    }
  };

  const handleDeleteStab = async (id) => {
    if (!confirm(t("confirm.deletePlant"))) return;
    try {
      await api.delete(`/tenant/produzione/stabilimenti/${id}`);
      toast.success("Stabilimento eliminato");
      if (selectedStabilimento?.id === id) {
        setSelectedStabilimento(null);
        setAree([]);
      }
      loadStabilimenti();
    } catch (error) {
      handleApiError(error, t("errors.deleteFailed"));
    }
  };

  // Area handlers
  const openCreateAreaDialog = () => {
    setEditingArea(null);
    const tipo = TIPI_AREA[0];
    setAreaForm({ nome: "", tipo: tipo.value, x: 10, y: 10, larghezza: 20, altezza: 15, colore: tipo.color, note: "" });
    setShowAreaDialog(true);
  };

  const openEditAreaDialog = (area) => {
    setEditingArea(area);
    setAreaForm({
      nome: area.nome,
      tipo: area.tipo,
      x: area.x,
      y: area.y,
      larghezza: area.larghezza,
      altezza: area.altezza,
      colore: area.colore || "#3b82f6",
      note: area.note || ""
    });
    setShowAreaDialog(true);
  };

  const handleSaveArea = async () => {
    if (!areaForm.nome) {
      toast.error("Nome obbligatorio");
      return;
    }
    try {
      if (editingArea) {
        await api.put(`/tenant/produzione/aree/${editingArea.id}`, { ...areaForm, stabilimento_id: selectedStabilimento.id });
        toast.success("Area aggiornata");
      } else {
        await api.post(`/tenant/produzione/stabilimenti/${selectedStabilimento.id}/aree`, areaForm);
        toast.success("Area creata");
      }
      setShowAreaDialog(false);
      loadAree();
    } catch (error) {
      handleApiError(error, t("errors.saveFailed"));
    }
  };

  const handleDeleteArea = async (id) => {
    if (!confirm(t("confirm.deleteArea"))) return;
    try {
      await api.delete(`/tenant/produzione/aree/${id}`);
      toast.success("Area eliminata");
      setSelectedArea(null);
      loadAree();
    } catch (error) {
      handleApiError(error, t("errors.deleteFailed"));
    }
  };

  // Layout drag handlers
  const handleAreaMouseDown = (area, e) => {
    e.stopPropagation();
    setSelectedArea(area);
    setDragging({
      id: area.id,
      startX: e.clientX,
      startY: e.clientY,
      origX: area.x,
      origY: area.y
    });
  };

  const handleMouseMove = (e) => {
    if (!dragging || !selectedStabilimento) return;
    
    const scale = zoom;
    const dx = (e.clientX - dragging.startX) / scale;
    const dy = (e.clientY - dragging.startY) / scale;
    
    // Aggiorna posizione visiva
    setAree(prev => prev.map(a => 
      a.id === dragging.id 
        ? { ...a, x: Math.max(0, Math.min(selectedStabilimento.larghezza_m - a.larghezza, dragging.origX + dx)),
                  y: Math.max(0, Math.min(selectedStabilimento.altezza_m - a.altezza, dragging.origY + dy)) }
        : a
    ));
  };

  const handleMouseUp = async () => {
    if (!dragging) return;
    
    const area = aree.find(a => a.id === dragging.id);
    if (area) {
      try {
        await api.put(`/tenant/produzione/aree/${area.id}`, {
          ...area,
          stabilimento_id: selectedStabilimento.id
        });
      } catch (error) {
        
        loadAree(); // Ricarica per annullare
      }
    }
    setDragging(null);
  };

  const getTipoInfo = (tipo) => TIPI_AREA.find(t => t.value === tipo) || TIPI_AREA[4];

  return (
    <div className="space-y-6" data-testid="produzione-stabilimenti-page">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/${tenant}/produzione`)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">{t("production.plants")}</h1>
            <p className="text-muted-foreground">Gestisci la planimetria degli stabilimenti produttivi</p>
          </div>
        </div>
        <Button onClick={openCreateStabDialog} data-testid="btn-nuovo-stabilimento">
          <Plus className="h-4 w-4 mr-2" />
          Nuovo Stabilimento
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Lista Stabilimenti */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-lg">Stabilimenti</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {stabilimenti.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                Nessuno stabilimento
              </p>
            ) : (
              stabilimenti.map((stab) => (
                <div
                  key={stab.id}
                  className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                    selectedStabilimento?.id === stab.id 
                      ? "bg-blue-50 border-blue-300" 
                      : "hover:bg-muted"
                  }`}
                  onClick={() => setSelectedStabilimento(stab)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="font-medium">{stab.nome}</div>
                      <div className="text-xs text-muted-foreground">{stab.codice}</div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => openEditStabDialog(stab)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Modifica
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          className="text-red-600"
                          onClick={() => handleDeleteStab(stab.id)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Elimina
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <Grid3X3 className="h-3 w-3" />
                    {stab.larghezza_m}m x {stab.altezza_m}m
                    <Badge variant="outline" className="ml-auto">
                      {stab.aree_count || 0} aree
                    </Badge>
                  </div>
                </div>
              ))
            )}
          </CardContent>
        </Card>

        {/* Editor Layout */}
        <Card className="lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <div>
              <CardTitle className="text-lg">
                {selectedStabilimento ? `Layout: ${selectedStabilimento.nome}` : "Seleziona uno stabilimento"}
              </CardTitle>
              {selectedStabilimento && (
                <CardDescription>
                  Dimensioni: {selectedStabilimento.larghezza_m}m x {selectedStabilimento.altezza_m}m
                </CardDescription>
              )}
            </div>
            {selectedStabilimento && (
              <div className="flex items-center gap-2">
                <Button variant="outline" size="icon" onClick={() => setZoom(z => Math.max(0.5, z - 0.1))}>
                  <ZoomOut className="h-4 w-4" />
                </Button>
                <span className="text-sm w-12 text-center">{Math.round(zoom * 100)}%</span>
                <Button variant="outline" size="icon" onClick={() => setZoom(z => Math.min(2, z + 0.1))}>
                  <ZoomIn className="h-4 w-4" />
                </Button>
                <Button variant="outline" size="icon" onClick={() => setZoom(1)}>
                  <RotateCcw className="h-4 w-4" />
                </Button>
                <Button onClick={openCreateAreaDialog}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nuova Area
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent>
            {!selectedStabilimento ? (
              <div className="flex items-center justify-center h-[400px] text-muted-foreground">
                <div className="text-center">
                  <Factory className="h-12 w-12 mx-auto opacity-50 mb-2" />
                  <p>{t("production.selectOrCreatePlant")}</p>
                </div>
              </div>
            ) : (
              <div className="overflow-auto border rounded-lg bg-slate-50" style={{ maxHeight: "500px" }}>
                <div
                  ref={canvasRef}
                  className="relative bg-white"
                  style={{
                    width: `${selectedStabilimento.larghezza_m * 5 * zoom}px`,
                    height: `${selectedStabilimento.altezza_m * 5 * zoom}px`,
                    backgroundImage: `
                      linear-gradient(rgba(0,0,0,0.05) 1px, transparent 1px),
                      linear-gradient(90deg, rgba(0,0,0,0.05) 1px, transparent 1px)
                    `,
                    backgroundSize: `${10 * zoom}px ${10 * zoom}px`
                  }}
                  onMouseMove={handleMouseMove}
                  onMouseUp={handleMouseUp}
                  onMouseLeave={handleMouseUp}
                >
                  {/* Aree */}
                  {aree.map((area) => {
                    const tipoInfo = getTipoInfo(area.tipo);
                    const IconComponent = tipoInfo.icon;
                    const isSelected = selectedArea?.id === area.id;
                    
                    return (
                      <div
                        key={area.id}
                        className={`absolute rounded-lg border-2 cursor-move transition-shadow ${
                          isSelected ? "shadow-lg ring-2 ring-blue-500" : "hover:shadow-md"
                        }`}
                        style={{
                          left: `${area.x * 5 * zoom}px`,
                          top: `${area.y * 5 * zoom}px`,
                          width: `${area.larghezza * 5 * zoom}px`,
                          height: `${area.altezza * 5 * zoom}px`,
                          backgroundColor: `${area.colore}20`,
                          borderColor: area.colore
                        }}
                        onMouseDown={(e) => handleAreaMouseDown(area, e)}
                        onDoubleClick={() => openEditAreaDialog(area)}
                      >
                        <div className="absolute inset-0 flex flex-col items-center justify-center p-2">
                          <IconComponent 
                            className="h-6 w-6 mb-1" 
                            style={{ color: area.colore }}
                          />
                          <span 
                            className="text-xs font-medium text-center line-clamp-2"
                            style={{ color: area.colore }}
                          >
                            {area.nome}
                          </span>
                          {area.materiali_count > 0 && (
                            <Badge 
                              variant="secondary" 
                              className="mt-1 text-[10px] h-5"
                            >
                              <Boxes className="h-3 w-3 mr-1" />
                              {area.materiali_count}
                            </Badge>
                          )}
                        </div>
                        {isSelected && (
                          <div className="absolute -top-2 -right-2 flex gap-1">
                            <Button
                              size="icon"
                              variant="secondary"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditAreaDialog(area);
                              }}
                            >
                              <Pencil className="h-3 w-3" />
                            </Button>
                            <Button
                              size="icon"
                              variant="destructive"
                              className="h-6 w-6"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteArea(area.id);
                              }}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
            
            {/* Legenda */}
            {selectedStabilimento && (
              <div className="flex flex-wrap items-center gap-4 mt-4 pt-4 border-t">
                <span className="text-sm font-medium">Legenda:</span>
                {TIPI_AREA.map((tipo) => {
                  const Icon = tipo.icon;
                  return (
                    <div key={tipo.value} className="flex items-center gap-1 text-sm">
                      <div 
                        className="w-4 h-4 rounded"
                        style={{ backgroundColor: `${tipo.color}40`, border: `2px solid ${tipo.color}` }}
                      />
                      <Icon className="h-3 w-3" style={{ color: tipo.color }} />
                      <span>{tipo.label}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Lista Aree (se stabilimento selezionato) */}
      {selectedStabilimento && aree.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Aree dello Stabilimento</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {aree.map((area) => {
                const tipoInfo = getTipoInfo(area.tipo);
                const Icon = tipoInfo.icon;
                return (
                  <div
                    key={area.id}
                    className="p-4 rounded-lg border hover:shadow-md transition-shadow cursor-pointer"
                    style={{ borderLeftWidth: 4, borderLeftColor: area.colore }}
                    onClick={() => setSelectedArea(area)}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Icon className="h-5 w-5" style={{ color: area.colore }} />
                      <span className="font-medium">{area.nome}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm text-muted-foreground">
                      <span>{tipoInfo.label}</span>
                      <span>{area.larghezza}m x {area.altezza}m</span>
                    </div>
                    {area.materiali_count > 0 && (
                      <Badge variant="outline" className="mt-2">
                        <Boxes className="h-3 w-3 mr-1" />
                        {area.materiali_count} materiali
                      </Badge>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dialog Stabilimento */}
      <Dialog open={showStabDialog} onOpenChange={setShowStabDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingStab ? t("production.editPlant") : t("production.newPlant")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.code')} *</Label>
                <Input
                  value={stabForm.codice}
                  onChange={(e) => setStabForm({...stabForm, codice: e.target.value})}
                  placeholder="es. STAB-01"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('common.name')} *</Label>
                <Input
                  value={stabForm.nome}
                  onChange={(e) => setStabForm({...stabForm, nome: e.target.value})}
                  placeholder="Nome stabilimento"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Indirizzo</Label>
              <Input
                value={stabForm.indirizzo}
                onChange={(e) => setStabForm({...stabForm, indirizzo: e.target.value})}
                placeholder="Via, Citta..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Larghezza (metri)</Label>
                <Input
                  type="number"
                  value={stabForm.larghezza_m}
                  onChange={(e) => setStabForm({...stabForm, larghezza_m: parseInt(e.target.value) || 100})}
                />
              </div>
              <div className="space-y-2">
                <Label>Altezza (metri)</Label>
                <Input
                  type="number"
                  value={stabForm.altezza_m}
                  onChange={(e) => setStabForm({...stabForm, altezza_m: parseInt(e.target.value) || 100})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea
                value={stabForm.note}
                onChange={(e) => setStabForm({...stabForm, note: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowStabDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSaveStab}>{editingStab ? "Aggiorna" : "Crea"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Area */}
      <Dialog open={showAreaDialog} onOpenChange={setShowAreaDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingArea ? t("production.editArea") : t("production.newArea")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('common.name')} *</Label>
                <Input
                  value={areaForm.nome}
                  onChange={(e) => setAreaForm({...areaForm, nome: e.target.value})}
                  placeholder="es. Area Saldatura"
                />
              </div>
              <div className="space-y-2">
                <Label>{t("common.type")}</Label>
                <Select 
                  value={areaForm.tipo}
                  onValueChange={(v) => {
                    const tipo = TIPI_AREA.find(t => t.value === v);
                    setAreaForm({...areaForm, tipo: v, colore: tipo?.color || "#3b82f6"});
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPI_AREA.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Pos. X (m)</Label>
                <Input
                  type="number"
                  value={areaForm.x}
                  onChange={(e) => setAreaForm({...areaForm, x: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Pos. Y (m)</Label>
                <Input
                  type="number"
                  value={areaForm.y}
                  onChange={(e) => setAreaForm({...areaForm, y: parseFloat(e.target.value) || 0})}
                />
              </div>
              <div className="space-y-2">
                <Label>Largh. (m)</Label>
                <Input
                  type="number"
                  value={areaForm.larghezza}
                  onChange={(e) => setAreaForm({...areaForm, larghezza: parseFloat(e.target.value) || 10})}
                />
              </div>
              <div className="space-y-2">
                <Label>Alt. (m)</Label>
                <Input
                  type="number"
                  value={areaForm.altezza}
                  onChange={(e) => setAreaForm({...areaForm, altezza: parseFloat(e.target.value) || 10})}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Colore</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="color"
                  value={areaForm.colore}
                  onChange={(e) => setAreaForm({...areaForm, colore: e.target.value})}
                  className="w-16 h-10 p-1"
                />
                <Input
                  value={areaForm.colore}
                  onChange={(e) => setAreaForm({...areaForm, colore: e.target.value})}
                  className="flex-1"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>{t("common.notes")}</Label>
              <Textarea
                value={areaForm.note}
                onChange={(e) => setAreaForm({...areaForm, note: e.target.value})}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAreaDialog(false)}>{t("common.cancel")}</Button>
            <Button onClick={handleSaveArea}>{editingArea ? "Aggiorna" : "Crea"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
